import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useGetIntegrationToken } from '../../../api/users'

export const IntegrationAuthContext = createContext({
  isAuthenticated: false,
  isLoading: false,
  tokenData: null,
  refetchToken: () => {},
  integration: null
})

export const useIntegrationAuth = () => {
  const context = useContext(IntegrationAuthContext)
  if (!context) {
    throw new Error('useIntegrationAuth must be used within IntegrationAuthProvider')
  }
  return context
}

export const useIntegrationAuthState = (integration, clientId, options = {}) => {
  const { enabled = !!integration && !!clientId } = options

  const [authDialogOpen, setAuthDialogOpen] = useState(false)

  const {
    data: tokenData,
    isLoading,
    error,
    refetch
  } = useGetIntegrationToken(integration, 201, clientId, { enabled })

  const requiresAuth = useMemo(() => !!error?.response?.requiresAuth, [error])
  const isAuthenticated = useMemo(() => !!tokenData && !requiresAuth, [tokenData, requiresAuth])

  const handleConnectClick = useCallback(() => {
    setAuthDialogOpen(true)
  }, [])

  const handleAuthSuccess = useCallback(() => {
    refetch()
  }, [refetch])

  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    tokenData,
    refetchToken: refetch,
    integration
  }), [isAuthenticated, isLoading, tokenData, refetch, integration])

  return {
    isLoading,
    isAuthenticated,
    requiresAuth,
    error,
    tokenData,
    refetch,
    contextValue,
    authDialogOpen,
    setAuthDialogOpen,
    handleConnectClick,
    handleAuthSuccess,
    integration
  }
}
